/**
 * Conductores: darle acceso a la app a un conductor.
 *
 * 🚨 Esta pantalla es lo que faltaba para poder arrancar el piloto. Hasta ahora
 * el PIN solo se podía crear entrando a la base de datos por consola de
 * comandos — algo que la oficina no puede hacer, y que además obliga a tener
 * acceso al servidor para una tarea de todos los días.
 *
 * Se busca por cédula o por placa, porque en la oficina muchas veces se sabe el
 * camión y no la cédula: «el de la UPO640».
 *
 * 🚨 El PIN se muestra UNA sola vez. No se guarda en ninguna parte legible: si
 * se pierde, se genera otro. Por eso la pantalla lo deja grande y quieto hasta
 * que la oficina lo cierre — no se va solo mientras se dicta por teléfono.
 */

import { useState, useEffect, useCallback } from 'react'
import { pedirConSesion, quienEntro } from './Entrar.tsx'
import { ComoVaElPiloto } from './ComoVaElPiloto.tsx'
import { usarPaginas, PieDeTabla } from './Tabla.tsx'
import { fmt } from './formato.ts'

interface Conductor {
  cedula: string
  nombre: string | null
  estado: string
  /** Cuándo entró por última vez. `null` = nunca ha entrado. */
  ultimo_acceso?: string | null
  tiene_acceso: boolean
  viajes_pendientes: string | number
  /** Todo lo que quedó abierto alguna vez, incluido lo que ya nadie ve. */
  abiertos_en_total?: string | number
  placas: string | null
  motivo_bloqueo: string | null
}

export function Conductores() {
  const [busca, setBusca] = useState('')
  const [vista, setVista] = useState<'calle' | 'piloto' | 'buscar'>('calle')
  const [lista, setLista] = useState<Conductor[] | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pinNuevo, setPinNuevo] = useState<{
    cedula: string
    pin: string
    nombre: string | null
  } | null>(null)
  const [generando, setGenerando] = useState<string | null>(null)
  const [cambiandoAcceso, setCambiandoAcceso] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  /**
   * Quitar o devolver el acceso.
   *
   * 🚨 NO borra ni anula nada: el conductor sigue en el sistema, sus viajes y
   * sus entregas siguen intactos. Solo deja de poder entrar a la app. Y corta
   * la sesión que ya tenga abierta — que es de lo que se trata cuando alguien
   * reporta un celular robado.
   */
  async function cambiarAcceso(c: Conductor) {
    const quitando = c.estado !== 'BLOQUEADO'
    const motivo = quitando
      ? window.prompt(
          '¿Por qué se le quita el acceso? (queda anotado)',
          'Reportó el celular perdido',
        )
      : ''
    if (quitando && motivo === null) return

    setCambiandoAcceso(c.cedula)
    setError(null)
    try {
      const r = await pedirConSesion<{ estado: string; mensaje: string }>(
        `/api/admin/conductores/${c.cedula}/acceso`,
        { method: 'POST', body: JSON.stringify({ permitir: !quitando, motivo }) },
      )
      setAviso(r.mensaje)
      setLista(
        (l) => l?.map((x) => (x.cedula === c.cedula ? { ...x, estado: r.estado } : x)) ?? null,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar el acceso')
    } finally {
      setCambiandoAcceso(null)
    }
  }

  async function buscar(e: React.FormEvent) {
    e.preventDefault()
    if (busca.trim().length < 3) {
      setError('Escribe al menos 3 números de la cédula, o la placa completa')
      return
    }
    setBuscando(true)
    setError(null)
    try {
      const r = await pedirConSesion<{ conductores: Conductor[] }>(
        `/api/admin/conductores?busca=${encodeURIComponent(busca.trim())}`,
      )
      setLista(r.conductores)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo buscar')
    } finally {
      setBuscando(false)
    }
  }

  /*
   * ⭐ El NOMBRE viaja con la clave.
   *
   * Esta lista se DICTA por teléfono —es la decisión de Luis para el piloto—.
   * El cuadro mostraba «Clave para la cédula 84.457.833» y cuatro números:
   * quien llama tenía que ir a buscar a quién correspondía esa cédula.
   */
  async function generarPin(cedula: string, nombre?: string | null) {
    setGenerando(cedula)
    setError(null)
    try {
      const r = await pedirConSesion<{ cedula: string; pin: string }>(
        `/api/admin/conductores/${cedula}/pin`,
        { method: 'POST', body: '{}' },
      )
      setPinNuevo({ ...r, nombre: nombre ?? null })
      setLista(
        (l) => l?.map((c) => (c.cedula === cedula ? { ...c, tiene_acceso: true } : c)) ?? null,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo generar el PIN')
    } finally {
      setGenerando(null)
    }
  }

  return (
    <section>
      <div className="tit-fila">
        <h1>Conductores</h1>
        <span>
          Cada uno entra a la app con su cédula y una clave de 4 números que le das aquí.
        </span>
      </div>

      {/*
       * ⛔⛔ EL ORDEN NO SE TOCA: título → una caja → tabla.
       *
       * 🚨 Antes esta pantalla eran DOS bloques sueltos, uno encima del otro:
       * un botón «Ver quién está en la calle hoy» que abría su propia tabla, y
       * más abajo otro título con otro buscador y otra tabla. Luis lo cazó
       * igual que en Entregas: «te prohíbo el diseño anterior de las vistas».
       * Ahora son dos pestañas de la MISMA caja.
       */}
      <div className="filtros-barra">
        <div className="pestanas">
          <button
            onClick={() => setVista('calle')}
            className={`pestana ${vista === 'calle' ? 'pestana-on' : ''}`}
          >
            En la calle hoy
          </button>
          <button
            onClick={() => setVista('piloto')}
            className={`pestana ${vista === 'piloto' ? 'pestana-on' : ''}`}
          >
            Cómo va el piloto
          </button>
          <button
            onClick={() => setVista('buscar')}
            className={`pestana ${vista === 'buscar' ? 'pestana-on' : ''}`}
          >
            Buscar un conductor
          </button>
        </div>

        {vista === 'buscar' && (
          <form onSubmit={buscar} className="filtros-fila">
            <div className="campo campo-ancho">
              <label htmlFor="c-busca">Cédula o placa</label>
              <input
                id="c-busca"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="1124406339 · UPO640"
              />
            </div>
            <button type="submit" disabled={buscando} className="btn-buscar">
              {buscando ? 'Buscando…' : 'Buscar'}
            </button>
          </form>
        )}

        <FechaDeCorte />
        <TelefonoDeLaOficina />

        <p className={`linea-msg ${error ? 'linea-msg-mal' : aviso ? 'linea-msg-bien' : ''}`}>
          {error ??
            aviso ??
            (vista === 'calle'
              ? 'Ordenados por viajes en curso. Empieza el piloto por los de arriba: son los que van a usar la app hoy mismo.'
              : vista === 'piloto'
                ? 'Los que ya tienen clave. Arriba los que hay que llamar: primero quien nunca ha entrado.'
                : 'Genera su clave de 4 números y se la dictas. La clave se muestra una sola vez; si se pierde, generas otra y la anterior deja de servir.')}
        </p>
      </div>

      {vista === 'calle' && <EnLaCalle alDarClave={(cedula, nombre) => void generarPin(cedula, nombre)} generando={generando} />}
      {vista === 'piloto' && (
        <ComoVaElPiloto alDarClave={(cedula, nombre) => void generarPin(cedula, nombre)} generando={generando} />
      )}

      {vista === 'buscar' && lista !== null && lista.length === 0 && (
        <p className="cargando">No hay ningún conductor con esa cédula ni esa placa.</p>
      )}

      {vista === 'buscar' && lista !== null && lista.length > 0 && (
        <div className="tabla-caja tabla-scroll">
        <table>
          <thead>
            <tr>
              <th>Conductor</th>
              <th>Camiones</th>
              <th>Lo que ve en la app</th>
              <th>Entra a la app</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((c) => (
              <tr key={c.cedula} className="border-b border-borde">
                <td className="py-3">
                  {/*
                    🚨 El nombre arriba y la cédula debajo, no al revés.
                    Durante meses el nombre no llegaba y la cédula era lo único
                    que había. Ya llegan los 590, y a una persona se le llama
                    por su nombre; la cédula es la referencia.
                  */}
                  {c.nombre ? (
                    <>
                      <div className="text-[16px] font-semibold">{c.nombre}</div>
                      <div className="num text-[13px] text-tinta-suave">
                        {conPuntos(c.cedula)}
                      </div>
                    </>
                  ) : (
                    <div className="num text-[16px] font-semibold">{conPuntos(c.cedula)}</div>
                  )}
                </td>
                <td className="num py-3 text-tinta-suave">{c.placas ?? '—'}</td>
                <td className="num py-3">
                  {Number(c.viajes_pendientes)}
                  {/* 🚨 El total abierto va DEBAJO y en chiquito. Medido: esta
                      columna decía 75 y la app del conductor decía 4. Ver los
                      dos números juntos es lo que evita que la oficina y el
                      conductor discutan sobre cuál es el bueno. */}
                  {Number(c.abiertos_en_total ?? 0) > Number(c.viajes_pendientes) && (
                    <small>
                      {Number(c.abiertos_en_total)} abiertos en el sistema
                    </small>
                  )}
                </td>
                <td className="py-3">
                  {c.estado === 'BLOQUEADO' ? (
                    <>
                      <span className="et et-mal">Sin acceso</span>
                      {c.motivo_bloqueo && <small>{c.motivo_bloqueo}</small>}
                    </>
                  ) : c.tiene_acceso && c.ultimo_acceso ? (
                    /* Ya entró: está usando la app de verdad. */
                    <>
                      <span className="rounded bg-[#EAF6EF] px-2 py-1 text-[14px] font-medium text-[#1F6E45]">
                        Usando la app
                      </span>
                      <div className="mt-1 text-[13px] text-tinta-suave">
                        Entró el{' '}
                        {new Date(c.ultimo_acceso).toLocaleDateString('es-CO', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </div>
                    </>
                  ) : c.tiene_acceso ? (
                    /*
                     * 🚨 Tiene clave pero NUNCA ha entrado. Antes esto se veía
                     * igual que el que ya está trabajando —los dos decían «ya
                     * tiene clave»— y son cosas muy distintas: aquí lo más
                     * probable es que la clave se generara y nunca le llegara.
                     * Medido: 2 de 7 estaban así.
                     */
                    <>
                      <span className="rounded bg-[#FDF4E3] px-2 py-1 text-[14px] font-medium text-[#A4600D]">
                        Tiene clave, no ha entrado
                      </span>
                      <div className="mt-1 text-[13px] text-tinta-suave">
                        Vuelve a generarla y mándasela.
                      </div>
                    </>
                  ) : (
                    <span className="et et-neu">
                      Todavía no
                    </span>
                  )}
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => void generarPin(c.cedula, c.nombre)}
                    disabled={generando !== null || c.estado === 'BLOQUEADO'}
                    className="h-10 rounded bg-navy px-4 text-[15px] font-semibold text-white disabled:opacity-45"
                  >
                    {generando === c.cedula
                      ? 'Generando…'
                      : c.tiene_acceso
                        ? 'Generar otra clave'
                        : 'Generar su clave'}
                  </button>
                </td>

                <td className="py-3 pl-2 text-right">
                  <button
                    onClick={() => void cambiarAcceso(c)}
                    disabled={cambiandoAcceso !== null}
                    className={`h-10 rounded px-4 text-[15px] font-semibold disabled:opacity-45 ${
                      c.estado === 'BLOQUEADO'
                        ? 'bg-[#EAF6EF] text-[#1F6E45]'
                        : 'bg-[#FDECEC] text-[#CA2B2B]'
                    }`}
                  >
                    {cambiandoAcceso === c.cedula
                      ? 'Un momento…'
                      : c.estado === 'BLOQUEADO'
                        ? 'Devolver el acceso'
                        : 'Quitar el acceso'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {pinNuevo && <PinGenerado datos={pinNuevo} alCerrar={() => setPinNuevo(null)} />}
    </section>
  )
}

/**
 * El PIN recién creado.
 *
 * Se queda quieto hasta que lo cierren: la oficina lo está dictando por
 * teléfono y un aviso que se desvanece solo sería exactamente lo contrario de
 * lo que se necesita.
 */
/**
 * La clave recién generada, en un cuadro sobre la pantalla.
 *
 * ⚠️ Va como capa fija a propósito: el botón que la genera puede estar arriba
 * del todo —en la tabla de «los que están en la calle»— o abajo, en el
 * buscador. Una clave que se muestra UNA sola vez no puede depender de dónde
 * quedó el desplazamiento.
 */
function PinGenerado({
  datos,
  alCerrar,
}: {
  datos: { cedula: string; pin: string; nombre: string | null }
  alCerrar: () => void
}) {
  const [copiado, setCopiado] = useState(false)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-[440px] rounded bg-white p-7 text-center">
        {datos.nombre ? (
          <>
            <p className="text-[19px] font-semibold text-tinta">{datos.nombre}</p>
            <p className="num text-[15px] text-tinta-suave">{conPuntos(datos.cedula)}</p>
          </>
        ) : (
          <p className="text-[16px] text-tinta-suave">
            Clave para la cédula <span className="num font-semibold">{conPuntos(datos.cedula)}</span>
          </p>
        )}

        <p className="num my-5 text-[56px] font-bold leading-none tracking-[0.12em]">{datos.pin}</p>

        <p className="mx-auto max-w-[34ch] text-[15px] text-tinta-suave">
          Díctasela al conductor. Con su cédula y estos 4 números entra a la app desde el celular.
          <strong className="font-semibold text-[#A4600D]">
            {' '}
            No la vuelves a ver: si se pierde, generas otra.
          </strong>
        </p>

        {/*
          🚨🚨 QUIÉN DIO ESTA CLAVE ES PARTE DE LA CADENA DE CUSTODIA.
          
          Con estos cuatro números el conductor entra a registrar entregas a
          nombre de la empresa. Si mañana aparece una entrega registrada por
          alguien que no debía tener acceso, la pregunta es **quién le dio la
          clave** — y con la clave compartida no hay a quién apuntar.
          
          MEDIDO el 24-ago-2026: **25 de 25 conductores con clave, y de ninguno
          se sabe quién se la dio.** La última, ese mismo día.
          
          Es el mismo hueco que se cerró en el diálogo de revisar entregas: el
          aviso existía en la pantalla de acceso, pero no aquí — que es el
          momento exacto en que se está entregando.
        */}
        {!quienEntro() && (
          <div className="mt-4 rounded-lg p-3 text-left" style={{ background: '#FDF4E3' }}>
            <p className="text-[15px] font-semibold" style={{ color: '#7D4A09' }}>
              No va a quedar registrado que tú se la diste
            </p>
            <p className="mt-0.5 text-[13px] text-tinta-suave">
              Entraste con la clave compartida. Con estos números el conductor registra entregas a
              nombre de la empresa: si algún día hay que preguntar quién le dio acceso, no habrá a
              quién. Entrando con tu correo, sí queda.
            </p>
          </div>
        )}

        {/*
         * 🚨 Copiar el mensaje listo, en vez de dictar cuatro números.
         *
         * Dictar una clave por teléfono en carretera es como se equivocan las
         * cosas: el conductor oye 6280 y teclea 6820, se bloquea a los intentos
         * y hay que empezar de nuevo. Y de paso hay que explicarle a dónde
         * entrar.
         *
         * Los conductores NO tienen celular en la base —medido: 0 de 589,
         * Vulcano no lo manda— así que no se puede mandar solo. Pero Luis ya
         * los tiene en su teléfono: esto le deja el mensaje armado para pegar.
         *
         * La dirección sale del navegador, no quemada: el día que cambie el
         * dominio, el mensaje cambia con él.
         */}
        <button
          onClick={() => void copiarMensaje(datos, setCopiado)}
          className="mt-6 h-12 w-full rounded bg-[#2a8553] text-[16px] font-semibold text-white"
        >
          {copiado ? '✓ Copiado — pégalo en WhatsApp' : 'Copiar el mensaje para el conductor'}
        </button>

        <button
          onClick={alCerrar}
          className="mt-2 h-12 w-full rounded border border-borde text-[16px] font-semibold"
        >
          Ya se la dicté
        </button>
      </div>
    </div>
  )
}

/** La cédula con puntos se lee; sin puntos, no. */
function conPuntos(cedula: string): string {
  const n = Number(cedula)
  return Number.isFinite(n) ? new Intl.NumberFormat('es-CO').format(n) : cedula
}

/**
 * Los conductores que están rodando HOY: los candidatos del piloto.
 *
 * 🚨 Sin esto, dar acceso era buscar de a uno por cédula — y había que saberse
 * de memoria a quién buscar. Medido: **588 de 589 conductores están POR_ACTIVAR
 * y ninguno tiene clave**, así que arrancar un piloto de cinco era adivinar.
 *
 * Van ordenados por cuántos viajes llevan en curso, que es lo que de verdad
 * importa para escoger: el que tiene 18 va a usar la app hoy mismo; el que
 * tiene 1 puede que no salga en toda la semana.
 */
function EnLaCalle({
  alDarClave,
  generando,
}: {
  alDarClave: (cedula: string, nombre: string | null) => void
  generando: string | null
}) {
  const [lista, setLista] = useState<
    | {
        cedula: string
        nombre: string | null
        viajes_en_curso: number
        placas: string | null
        estado: string
        tiene_clave: boolean
        ultimo_acceso: string | null
      }[]
    | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  /** Los totales de verdad: la lista viene con LIMIT 60. */
  const [totales, setTotales] = useState<{
    enLaCalle: number
    conClave: number
    viajes: number
  } | null>(null)

  useEffect(() => {
    if (lista !== null) return
    void (async () => {
      try {
        const r = await pedirConSesion<{
          conductores: typeof lista
          totales?: { enLaCalle: number; conClave: number; viajes: number }
        }>('/api/admin/conductores/en-la-calle')
        setLista(r.conductores ?? [])
        setTotales(r.totales ?? null)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo cargar')
      }
    })()
  }, [lista])

  const conClave = (lista ?? []).filter((c) => c.tiene_clave).length
  const pag = usarPaginas(lista ?? [])

  return (
    <>
      {/*
       * ⭐ El termómetro del piloto, arriba de la tabla: de los que están
       * rodando hoy, cuántos ya tienen clave. Si ese número no sube, la app no
       * está llegando a la calle.
       */}
      {lista && lista.length > 0 && (
        <div className="barra-avance">
          {/*
            🚨 EL DENOMINADOR SALE DE UN count(*), NO DEL LARGO DE LA LISTA.
            Medido el 24-ago-2026: decía «12 de 60» y la verdad era «16 de
            108». La lista trae LIMIT 60, así que contarla hacía creer que
            faltaban 48 conductores cuando faltan 92.
          */}
          <div className="barra-avance-riel">
            <span
              style={{
                width: `${Math.round(((totales?.conClave ?? conClave) / (totales?.enLaCalle ?? lista.length)) * 100)}%`,
              }}
            />
          </div>
          <p>
            {/*
              🚨 «CON CARGA HOY», no «en la calle».
              
              El Tablero dice «190 conductores en la calle» y aquí salen 108.
              Los dos son correctos —el Tablero cuenta toda la operación y esto
              solo lo que el conductor **vería en su app**, dentro de la fecha
              de corte— pero con las mismas palabras obliga a adivinar cuál
              vale. Cambiar tres palabras cuesta menos que explicar la
              diferencia cada vez.
            */}
            <b className="num">{totales?.conClave ?? conClave}</b> de{' '}
            <b className="num">{totales?.enLaCalle ?? lista.length}</b> conductores con carga hoy
            ya tienen clave
            {/*
              ⭐ Y CUÁNTA CARGA HAY EN JUEGO. Sin esto, «16 de 108» es una
              proporción; con los viajes al lado es una decisión: se ve de una
              si vale la pena sentarse a repartir claves esta tarde.
            */}
            {totales && totales.enLaCalle > totales.conClave && (
              <span className="block text-[13px] text-tinta-suave">
                Los {fmt(totales.enLaCalle - totales.conClave)} que faltan llevan carga hoy. Abajo
                están ordenados por cuántos viajes traen: empieza por los de arriba.
              </span>
            )}
          </p>
        </div>
      )}

      {error && <p className="linea-msg linea-msg-mal">{error}</p>}
      {lista === null && !error && <p className="cargando">Buscando…</p>}

      {lista && lista.length > 0 && (
        <div className="tabla-caja tabla-scroll">
        <table>
          <thead>
            <tr>
              <th>Conductor</th>
              <th>Viajes en curso</th>
              <th>Placa</th>
              <th>Ya entró</th>
              <th style={{ textAlign: 'right' }}>Clave</th>
            </tr>
          </thead>
          <tbody>
            {(pag.enPantalla as NonNullable<typeof lista>).map((c) => (
              <tr key={c.cedula} className="border-b border-[#EDEFF3]">
                {/* El nombre manda; la cedula es la referencia. Vulcano los
                    empezo a mandar el 21-ago y ya estan los 590. */}
                <td className="px-4 py-2">
                  {c.nombre ? (
                    <>
                      <div className="font-medium">{c.nombre}</div>
                      <div className="num text-[13px] text-tinta-suave">{conPuntos(c.cedula)}</div>
                    </>
                  ) : (
                    <span className="num font-medium">{conPuntos(c.cedula)}</span>
                  )}
                </td>
                <td className="num px-4 py-2">{c.viajes_en_curso}</td>
                <td className="num px-4 py-2">{c.placas ?? '—'}</td>
                <td className=" text-tinta-suave">
                  {c.ultimo_acceso
                    ? new Date(c.ultimo_acceso).toLocaleDateString('es-CO', {
                        day: 'numeric',
                        month: 'short',
                      })
                    : c.tiene_clave
                      ? 'Todavía no'
                      : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    onClick={() => alDarClave(c.cedula, c.nombre)}
                    disabled={generando === c.cedula}
                    className="btn-tabla btn-tabla-fuerte"
                  >
                    {generando === c.cedula
                      ? 'Generando…'
                      : c.tiene_clave
                        ? 'Generar otra'
                        : 'Darle clave'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PieDeTabla {...pag} queSon="conductores en la calle" />
        </div>
      )}
    </>
  )
}

/**
 * Arma el mensaje para el conductor y lo deja copiado.
 *
 * La dirección se saca de `location.host`, nunca quemada: Luis avisó que el
 * dominio va a cambiar, y un mensaje con la dirección vieja manda al conductor
 * a una página que ya no existe.
 */
async function copiarMensaje(
  datos: { cedula: string; pin: string; nombre: string | null },
  setCopiado: (v: boolean) => void,
): Promise<void> {
  /*
   * 🚨 UN SOLO ENLACE, y es el de instalar.
   *
   * Antes el mensaje mandaba primero a `/app` y dejaba `/instalar` en la última
   * línea, como nota al pie. El conductor toca el primer enlace que ve y nunca
   * llega al segundo — así que se queda sin instalarla, sin ícono y sin la
   * única forma de garantizar que abra en una zona sin cobertura.
   *
   * La página de instalar ya resuelve las dos: tiene «Abrir la app ahora» a un
   * toque y el botón que la deja con su ícono. Dos enlaces solo le dan la
   * oportunidad de escoger el que no le conviene.
   *
   * 🚨 Y se le avisa que la primera vez le van a pedir cambiar la clave: un
   * conductor de verdad se quedó un día entero trancado en esa pantalla por no
   * saber que venía.
   */
  const nombreCorto = datos.nombre?.trim().split(/\s+/)[0] ?? ''
  const texto = [
    `${nombreCorto ? nombreCorto + ', ya' : 'Ya'} puedes registrar tus entregas con SymTrack.`,
    '',
    `Ábrelo aquí: ${location.host}/instalar`,
    'Esa página te lo deja como una app en el celular, con su ícono.',
    '',
    `Tu cédula: ${datos.cedula}`,
    `Tu clave: ${datos.pin}`,
    'La primera vez te pide cambiarla por una que solo sepas tú. Es un momento, y sin eso no ves tus viajes.',
    '',
    // 🚨 El papel se llama CUMPLIDO. Luis: «el cumplido es una hoja que la
    // persona que recibe me entrega y le tengo que tomar la foto».
    // 🚨 Decía «y la firma de quien recibe». La firma quedó EN PAUSA: al
    // conductor no le firman nada, le ENTREGAN la hoja. Prometérselo en el
    // mensaje de bienvenida lo manda a buscar un paso que no existe.
    'Ahí ves tus viajes, escribes quién recibió y le tomas la foto a la hoja del cumplido que te entregan.',
    'Funciona sin señal: lo que registres sube solo cuando vuelva la cobertura.',
    '',
  ].join('\n')

  try {
    await navigator.clipboard.writeText(texto)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 4000)
  } catch {
    /*
     * El portapapeles puede estar bloqueado —pasa sin HTTPS o con permisos
     * cerrados—. Antes de dejarlo sin salida, se selecciona el texto en un
     * campo para que pueda copiarlo a mano.
     */
    const caja = document.createElement('textarea')
    caja.value = texto
    caja.style.position = 'fixed'
    caja.style.opacity = '0'
    document.body.appendChild(caja)
    caja.select()
    try {
      document.execCommand('copy')
      setCopiado(true)
      setTimeout(() => setCopiado(false), 4000)
    } catch {
      window.prompt('Copia este mensaje y mándaselo al conductor:', texto)
    }
    caja.remove()
  }
}

/**
 * Desde cuándo muestra viajes la app del conductor.
 *
 * 🚨 Lo pidió Luis: la app arranca ahora y en Vulcano hay meses de viajes
 * viejos que casi seguro ya se entregaron. Sin un corte, el conductor abre la
 * app y lo primero que ve son doce pendientes de los cuales ocho son ruido.
 *
 * ⛔ No borra ni marca NADA. Lo anterior al corte sigue en su pestaña «Viajes
 * anteriores», y lo que el conductor ya haya tocado se queda entre sus
 * pendientes aunque sea viejo: puede llevar esa carga encima ahora mismo.
 */
/**
 * El teléfono al que llaman los conductores desde la app.
 *
 * 🚨 La app les dice «llama a la oficina» en tres avisos —el manifiesto que
 * falta, el que no abre, y el viaje que la empresa ya cerró— y hasta ahora no
 * les daba con qué llamar. Parado en un retén, nadie se pone a buscar un
 * número en la agenda.
 *
 * ⛔ El número NO se inventa ni se deja escrito en el código: se escribe aquí.
 * Mientras esté vacío, la app no le muestra ningún botón a nadie.
 */
function TelefonoDeLaOficina() {
  const [guardado, setGuardado] = useState<string | null>(null)
  const [tel, setTel] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [msj, setMsj] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    try {
      const r = await pedirConSesion<{ telefono: string | null }>('/api/admin/telefono-oficina')
      setGuardado(r.telefono)
      setTel(r.telefono ?? '')
    } catch {
      /* Si no carga, el bloque no promete nada. */
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  async function guardar(valor: string | null) {
    setGuardando(true)
    setMsj(null)
    try {
      const r = await pedirConSesion<{ telefono: string | null; mensaje?: string }>(
        '/api/admin/telefono-oficina',
        { method: 'POST', body: JSON.stringify({ telefono: valor }) },
      )
      setMsj(
        r.telefono
          ? `Listo. A los conductores les sale el botón de llamar al ${r.telefono}.`
          : (r.mensaje ?? 'Los conductores dejan de ver el botón.'),
      )
      await cargar()
    } catch (e) {
      setMsj(e instanceof Error ? e.message : 'No se pudo guardar')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="corte">
      <div className="corte-texto">
        <p className="corte-titulo">A qué número llaman los conductores</p>
        <p className="corte-pie">
          {guardado
            ? `La app les muestra el botón «Llamar a la oficina» en los avisos que les dicen que llamen. Marca al ${guardado}.`
            : 'La app les dice «llama a la oficina» y no les da con qué. Escribe el número aquí y les sale el botón para marcar de una.'}
        </p>
      </div>

      <div className="corte-controles">
        <input
          type="tel"
          inputMode="tel"
          value={tel}
          placeholder="3001234567"
          onChange={(e) => setTel(e.target.value)}
          className="corte-fecha"
          aria-label="Teléfono de la oficina"
        />
        <button
          onClick={() => void guardar(tel.trim() || null)}
          disabled={guardando || tel.trim() === (guardado ?? '')}
          className="boton boton-primario"
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {guardado && (
          <button
            onClick={() => void guardar(null)}
            disabled={guardando}
            className="boton boton-secundario"
          >
            Quitar el número
          </button>
        )}
      </div>

      {msj && <p className="corte-cuenta">{msj}</p>}
    </section>
  )
}

function FechaDeCorte() {
  const [fecha, setFecha] = useState('')
  const [guardada, setGuardada] = useState<string | null>(null)
  const [fuera, setFuera] = useState(0)
  /* Lo que de verdad decide la fecha: cuánta GENTE queda con trabajo visible. */
  const [conTrabajo, setConTrabajo] = useState(0)
  const [sinNada, setSinNada] = useState(0)
  const [dentro, setDentro] = useState(0)
  const [guardando, setGuardando] = useState(false)
  const [msj, setMsj] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    try {
      const r = await pedirConSesion<{
        fecha: string | null
        dentro: number
        fuera: number
        conductoresConTrabajo: number
        conductoresSinNada: number
      }>('/api/admin/fecha-corte')
      setGuardada(r.fecha)
      setFecha(r.fecha ?? '')
      setDentro(r.dentro)
      setFuera(r.fuera)
      setConTrabajo(r.conductoresConTrabajo)
      setSinNada(r.conductoresSinNada)
    } catch {
      /* Si no carga, el bloque simplemente no promete nada. */
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  async function guardar(valor: string | null) {
    setGuardando(true)
    setMsj(null)
    try {
      const r = await pedirConSesion<{ fecha: string | null; mensaje?: string }>(
        '/api/admin/fecha-corte',
        { method: 'POST', body: JSON.stringify({ fecha: valor }) },
      )
      setMsj(
        r.fecha
          ? `Listo. Los conductores ven sus viajes desde el ${r.fecha}.`
          : (r.mensaje ?? 'Los conductores vuelven a ver todo.'),
      )
      await cargar()
    } catch (e) {
      setMsj(e instanceof Error ? e.message : 'No se pudo guardar')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="corte">
      <div className="corte-texto">
        <p className="corte-titulo">La app arranca desde esta fecha</p>
        <p className="corte-pie">
          {guardada
            ? `Cada conductor ve sus viajes despachados del ${guardada} en adelante. Lo de antes queda en «Viajes anteriores» — no se borra nada, y lo que él ya empezó sigue entre sus pendientes.`
            : 'Sin fecha, cada conductor ve TODOS sus viajes abiertos, incluidos los de hace meses que seguramente ya entregó antes de tener la app.'}
        </p>
      </div>

      <div className="corte-controles">
        <input
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          className="corte-fecha"
          aria-label="Fecha desde la que la app muestra viajes"
        />
        <button
          onClick={() => void guardar(fecha || null)}
          disabled={guardando || fecha === (guardada ?? '')}
          className="boton boton-primario"
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {guardada && (
          <button
            onClick={() => void guardar(null)}
            disabled={guardando}
            className="boton boton-secundario"
          >
            Quitar el corte
          </button>
        )}
      </div>

      {/* Lo que cambia de verdad, en viajes: el efecto ANTES de decidir. */}
      <p className="corte-cuenta">
        {guardada ? (
          <>
            Los conductores ven <strong>{dentro.toLocaleString('es-CO')}</strong> viajes y{' '}
            <strong>{fuera.toLocaleString('es-CO')}</strong> quedan en «Viajes anteriores».
          </>
        ) : (
          <>
            Hoy hay <strong>{dentro.toLocaleString('es-CO')}</strong> viajes abiertos repartidos
            entre todos los conductores.
          </>
        )}
      </p>

      {/*
       * 🚨 El número que de verdad decide la fecha: cuánta GENTE queda con algo
       * que hacer. Medido: con el 1 de julio serían 118 conductores con trabajo
       * visible; con el 15 de agosto, 75. Un conductor que abre la app y ve un
       * cero no la vuelve a abrir.
       */}
      <p className="corte-gente">
        <strong>{conTrabajo}</strong> conductores abren la app y ven trabajo.{' '}
        {sinNada > 0 && (
          <span className="corte-ojo">
            {sinNada} la abren y no ven nada — la app se los explica, pero conviene saberlo.
          </span>
        )}
      </p>

      {msj && <p className="corte-msj">{msj}</p>}
    </section>
  )
}
